import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, finalize, Observable, shareReplay, switchMap, take, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../services/auth.service';
import { RETRIED_AFTER_REFRESH } from './http-context';

/**
 * Refresh de token partage entre toutes les requetes concurrentes.
 *
 * Tant qu'un refresh est en cours, les 401 concurrents s'abonnent au MEME flux
 * au lieu de declencher chacun leur propre refresh (evite la race condition ou
 * les requetes en echec etaient perdues). Une fois le refresh termine, chaque
 * requete en attente est rejouee.
 */
let refreshInFlight$: Observable<unknown> | null = null;

/**
 * Interceptor HTTP pour l'authentification JWT via cookies httpOnly.
 *
 * Securite :
 * - Ajoute withCredentials pour envoyer les cookies httpOnly automatiquement
 * - PAS de header Authorization (les tokens ne sont plus exposes cote client)
 * - Gere le refresh automatique sur 401 via le cookie refresh httpOnly,
 *   serialise entre toutes les requetes concurrentes
 */
export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
    const authService = inject(AuthService);

    const isApiRequest = req.url.startsWith(environment.apiUrl);
    // Envoyer les cookies httpOnly automatiquement — pas de header Authorization
    const authReq = isApiRequest ? req.clone({ withCredentials: true }) : req;

    return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
            // `/auth/logout/` en fait partie : un 401 sur la deconnexion relancait un refresh,
            // dont l'echec rappelait `logout()`, en boucle (audit R-04).
            const isAuthEndpoint = /\/auth\/(token|logout)/.test(req.url);
            const alreadyRetried = req.context.get(RETRIED_AFTER_REFRESH);

            // Ne pas tenter de refresh : hors 401, sur les endpoints d'auth eux-memes,
            // ou sur une requete deja rejouee une fois (anti-boucle).
            if (error.status !== 401 || isAuthEndpoint || alreadyRetried) {
                return throwError(() => error);
            }

            // Demarrer un refresh partage si aucun n'est en cours.
            // `refreshToken()` gere lui-meme le logout en cas d'echec (source unique).
            if (!refreshInFlight$) {
                refreshInFlight$ = authService.refreshToken().pipe(
                    finalize(() => {
                        refreshInFlight$ = null;
                    }),
                    shareReplay(1),
                );
            }

            return refreshInFlight$.pipe(
                take(1),
                switchMap(() => {
                    // Rejouer la requete en la marquant pour interdire un second refresh.
                    const retryReq = req.clone({
                        withCredentials: true,
                        context: req.context.set(RETRIED_AFTER_REFRESH, true),
                    });

                    return next(retryReq);
                }),
            );
        }),
    );
};
